import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link, Navigate } from "react-router-dom";
import { db } from "../utils/firebase";
import { doc, getDoc } from "firebase/firestore";
import { LuCircleCheck } from "react-icons/lu";

export default function OrderConfirmation() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { orderId } = location.state || {};
  const [order,   setOrder]   = useState(null);
  const [loadErr, setLoadErr] = useState(false);

  if (!orderId) return <Navigate to="/" replace />;

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "orders", orderId));
        if (snap.exists()) setOrder({ id: snap.id, ...snap.data() });
        else setLoadErr(true);
      } catch (err) { console.error(err); setLoadErr(true); }
    })();
  }, [orderId]);

  if (loadErr) {
    return (
      <div className="min-h-screen bg-brand-surface flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-slate-500 mb-4">Unable to load order details.</p>
          <Link to="/orders" className="text-brand-amber font-bold hover:underline">View My Orders →</Link>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-brand-surface flex items-center justify-center">
        <div className="animate-pulse text-slate-400 text-sm">Loading order details…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-surface font-sans flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-lg">
        {/* Success icon */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-3xl bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-6">
            <LuCircleCheck size={40} className="text-green-500" />
          </div>
          <h1 className="text-3xl font-black text-brand-navy tracking-tight mb-2">Order Placed!</h1>
          <p className="text-slate-400 text-sm">Thank you for your wholesale order. We'll be in touch shortly.</p>
        </div>

        {/* Order card */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7 mb-5">
          <div className="flex justify-between items-center mb-5">
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Order ID</p>
              <p className="font-black text-brand-navy font-mono">#{order.id.slice(-10).toUpperCase()}</p>
            </div>
            <span className="text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1 rounded-full">Processing</span>
          </div>

          <div className="border-t border-slate-100 pt-5 space-y-3 mb-5">
            {order.items.map((item, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-brand-navy font-medium">{item.name} <span className="text-slate-400">× {item.quantity}</span></span>
                <span className="font-bold text-brand-navy">${Number(item.price).toFixed(2)}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-100 pt-4 flex justify-between">
            <span className="font-black text-brand-navy">Total</span>
            <span className="font-black text-brand-navy text-lg">${Number(order.total).toFixed(2)}</span>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate("/orders")}
            className="flex-1 bg-brand-navy hover:bg-brand-amber transition-colors text-white font-bold py-3.5 rounded-xl text-center"
          >
            View My Orders
          </button>
          <Link
            to="/products/disposable-vapes"
            className="flex-1 border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white transition-colors font-bold py-3.5 rounded-xl text-center"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
